import React from "react";
import { Box, Typography, Grid, CircularProgress } from "@mui/material";
import { useQuery } from "react-query";
import { fetchStatistiche } from "../../api/statistiche";
import StatCard from "../../components/statistiche/StatCard";
import SideBar from "../../components/sidebar/Sidebar";
import BarChartIcon from "@mui/icons-material/BarChart";
import InventoryIcon from "@mui/icons-material/Inventory";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import EuroIcon from "@mui/icons-material/Euro";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import DescriptionIcon from "@mui/icons-material/Description";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import VerifiedIcon from "@mui/icons-material/Verified";

const StatistichePage: React.FC = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["statistiche"],
    queryFn: fetchStatistiche,
    refetchInterval: 60000, // Aggiorna ogni minuto
  });

  if (isLoading) {
    return (
      <div className="home">
        <SideBar />
        <div className="homeContainer">
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="home">
        <SideBar />
        <div className="homeContainer">
          <Box p={3}>
            <Typography color="error">Errore nel caricamento delle statistiche</Typography>
          </Box>
        </div>
      </div>
    );
  }

  const stats = data.data;

  return (
    <div className="home">
      <SideBar />
      <div className="homeContainer">
        <Box p={3}>
          <Box sx={{ mb: 3 }}>
            <Box sx={{ 
              display: "flex", 
              alignItems: "center", 
              gap: 2,
              mb: 2,
              pb: 2,
              borderBottom: "2px solid",
              borderColor: "primary.main"
            }}>
              <BarChartIcon sx={{ fontSize: 40, color: "primary.main" }} />
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 600,
                  color: "primary.main",
                  letterSpacing: "0.5px"
                }}
              >
                Statistiche
              </Typography>
            </Box>
          </Box>

          {/* Riga 1 - Metriche Principali */}
          <Typography variant="h6" sx={{ mb: 2, color: "#1976d2", fontWeight: "bold", fontStyle: "italic" }}>
            Metriche Principali
          </Typography>
          <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Totale Beni"
            value={stats.totale_beni}
            icon={<InventoryIcon sx={{ color: "white", fontSize: 30 }} />}
            color="#2196f3"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Beni in Valutazione"
            value={stats.beni_valutazione}
            icon={<HourglassEmptyIcon sx={{ color: "white", fontSize: 30 }} />}
            color="#ff9800"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Beni Approvati"
            value={stats.beni_approvati}
            icon={<VerifiedIcon sx={{ color: "white", fontSize: 30 }} />}
            color="#00bcd4"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Beni Venduti"
            value={stats.beni_venduti}
            icon={<CheckCircleIcon sx={{ color: "white", fontSize: 30 }} />}
            color="#4caf50"
          />
        </Grid>
      </Grid>

      {/* Riga 2 - Performance Mensili */}
      <Typography variant="h6" sx={{ mb: 2, color: "#1976d2", fontWeight: "bold", fontStyle: "italic" }}>
        Performance Mese Corrente
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Vendite Mese"
            value={stats.vendite_mese}
            icon={<TrendingUpIcon sx={{ color: "white", fontSize: 30 }} />}
            color="#00bcd4"
            trend={stats.trend_vendite_mese}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Incasso Mese"
            value={`€ ${stats.incasso_mese.toLocaleString("it-IT", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`}
            icon={<AttachMoneyIcon sx={{ color: "white", fontSize: 30 }} />}
            color="#4caf50"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Nuovi Clienti Mese"
            value={stats.nuovi_clienti_mese}
            icon={<PersonAddIcon sx={{ color: "white", fontSize: 30 }} />}
            color="#3f51b5"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Nuovi Contratti Mese"
            value={stats.nuovi_contratti_mese}
            icon={<DescriptionIcon sx={{ color: "white", fontSize: 30 }} />}
            color="#673ab7"
          />
        </Grid>
      </Grid>

      {/* Riga 3 - Stato Operativo */}
      <Typography variant="h6" sx={{ mb: 2, color: "#1976d2", fontWeight: "bold", fontStyle: "italic" }}>
        Stato Operativo
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Beni da Restituire"
            value={stats.beni_da_restituire}
            icon={<AssignmentReturnIcon sx={{ color: "white", fontSize: 30 }} />}
            color="#f44336"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Contratti Attivi"
            value={stats.contratti_attivi}
            icon={<FolderOpenIcon sx={{ color: "white", fontSize: 30 }} />}
            color="#009688"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Valore Totale Vendite"
            value={`€ ${stats.valore_totale_vendite.toLocaleString("it-IT", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`}
            icon={<EuroIcon sx={{ color: "white", fontSize: 30 }} />}
            color="#9c27b0"
          />
        </Grid>
      </Grid>
        </Box>
      </div>
    </div>
  );
};

export default StatistichePage;
